/**
 * Bridges actions taken in the native call UI (CallKit / ConnectionService) to
 * the in-app call screen — e.g. the user tapping "end" or "mute" on the lock
 * screen. The global callkeep listeners (see [services/calls/callKeep.ts]) are
 * registered once at app launch and have no reference to the mounted screen, so
 * they emit here and the screen subscribes. Mirrors the singleton-emitter
 * pattern in [services/auth/authEvents.ts].
 */

type CallEventPayloads = {
    /** User ended the call from the system call UI. */
    endCall: { callUUID: string };
    /** User toggled mute from the system call UI. */
    setMuted: { callUUID: string; muted: boolean };
};

type CallEventName = keyof CallEventPayloads;
type CallListener<E extends CallEventName> = (payload: CallEventPayloads[E]) => void;

class CallEventEmitter {
    private listeners: { [E in CallEventName]?: CallListener<E>[] } = {};

    on<E extends CallEventName>(event: E, listener: CallListener<E>): () => void {
        const arr = (this.listeners[event] ??= []) as CallListener<E>[];
        arr.push(listener);
        return () => {
            this.listeners[event] = arr.filter((l) => l !== listener) as CallEventEmitter['listeners'][E];
        };
    }

    emit<E extends CallEventName>(event: E, payload: CallEventPayloads[E]): void {
        (this.listeners[event] as CallListener<E>[] | undefined)?.forEach((l) => l(payload));
    }
}

export const callEvents = new CallEventEmitter();
